import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  UserGroupIcon,
  DocumentTextIcon,
  CalendarIcon,
  CloudIcon,
  PlusIcon,
  TrashIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowLeftIcon,
  PencilIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import api from '../../services/api';
import { useDynamicPermissions } from '../../hooks/useDynamicPermissions';
import { dateToUTCNoon } from '../../utils/dateHelpers';
import {
  AWS_ALL_REGION_CODES,
  AWS_REGION_OPTIONS,
  AWS_SELECT_ALL_REGIONS_VALUE
} from '../../constants/awsRegions';
import { normalizeAwsDetailsForApi } from '../../utils/engagementAwsDetails';
import {
  GCP_REGION_OPTIONS,
  GCP_SELECT_ALL_REGIONS_VALUE,
  type GcpRegionOption
} from './gcpRegions';
import { normalizeGcpDetailsForApi } from './engagementGcpDetails';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import PatchingServicesSection, {
  defaultPatchingServicesFormState,
  patchingServicesFromApi,
  patchingServicesToApiPayload,
  PatchingServicesFormState,
  validatePatchingServicesForm
} from '../../components/engagements/PatchingServicesSection';
import { RequiredFieldLabel } from '../../components/forms';
import ServiceProvidersTab from './ServiceProvidersTab';

const EngagementEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState('details');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [engagement, setEngagement] = useState<any>(null);
  const [groups, setGroups] = useState<any[]>([]);
  const [availablePlans, setAvailablePlans] = useState<any[]>([]);
  const [assignedPlans, setAssignedPlans] = useState<any[]>([]);
  const { formatDate } = useDateFormatter();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    cloudProvider: '',
    description: '',
    billingType: '',
    currency: '',
    startDate: '',
    endDate: '',
    totalBudget: null as number | null,
    contractValue: null as number | null,
    minimumServiceFee: 0,
    platformCost: 0,
    billable: true,
    isServiceCreditEngagement: false,
    complimentaryHours: null as number | null,
    status: '',
    // Email-to-Case Configuration
    emailCaseCreationEnabled: false,
    emailMatchAddress: '',
    emailMatchSubject: '',
    emailMatchBody: '',
    emailMatchToRecipients: ''
  });

  const [dateError, setDateError] = useState('');
  const [minimumServiceFeeError, setMinimumServiceFeeError] = useState('');
  const [platformCostError, setPlatformCostError] = useState('');
  const [cloudProviderFieldErrors, setCloudProviderFieldErrors] = useState<Record<string, string>>({});
  const [patchingServices, setPatchingServices] = useState<PatchingServicesFormState>(
    defaultPatchingServicesFormState()
  );

  const [cloudProviderData, setCloudProviderData] = useState({
    azureDetails: {
      tenantId: '',
      clientId: '',
      clientSecret: '',
      subscriptionId: '',
      authorityUrl: ''
    },
    awsDetails: {
      accountId: '',
      region: '',
      accessKeyId: '',
      secretAccessKey: '',
      vpcId: '',
      iamRole: ''
    },
    gcpDetails: {
      projectId: '',
      region: '',
      serviceAccount: '',
      zone: ''
    }
  });

  const { can, loading: permissionsLoading } = useDynamicPermissions();
  const hasPermission = can('engagements', 'update');

  const fetchEngagement = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/engagements/${id}`);
      const engagementData = response.data;
      setEngagement(engagementData);

      // Populate form with existing data
      const startDate = engagementData.startDate ? new Date(engagementData.startDate).toISOString().split('T')[0] : '';
      const endDate = engagementData.endDate ? new Date(engagementData.endDate).toISOString().split('T')[0] : '';

      setFormData({
        name: engagementData.name,
        cloudProvider: engagementData.cloudProvider || '',
        description: engagementData.description || '',
        billingType: engagementData.billingType,
        currency: engagementData.currency,
        startDate,
        endDate,
        totalBudget: engagementData.totalBudget,
        contractValue: engagementData.contractValue,
        minimumServiceFee: Number(engagementData.minimumServiceFee) || 0,
        platformCost: Number(engagementData.platformCost) || 0,
        billable: engagementData.billable !== false,
        isServiceCreditEngagement: !!engagementData.isServiceCreditEngagement,
        complimentaryHours:
          engagementData.complimentaryHours != null
            ? Number(engagementData.complimentaryHours)
            : null,
        status: engagementData.status,
        // Email-to-Case Configuration
        emailCaseCreationEnabled: engagementData.emailCaseCreationEnabled || false,
        emailMatchAddress: engagementData.emailMatchAddress || '',
        emailMatchSubject: engagementData.emailMatchSubject || '',
        emailMatchBody: engagementData.emailMatchBody || '',
        emailMatchToRecipients: engagementData.emailMatchToRecipients || ''
      });

      setPatchingServices(patchingServicesFromApi(engagementData.patchingServices));

      // Set assigned plans from engagement data
      setAssignedPlans(engagementData.ServicePlanAssignment || []);

      // Populate cloud provider data
      setCloudProviderData({
        azureDetails: engagementData.azureDetails || {
          tenantId: '',
          clientId: '',
          clientSecret: '',
          subscriptionId: '',
          authorityUrl: ''
        },
        awsDetails: {
          accountId: '',
          region: '',
          accessKeyId: '',
          secretAccessKey: '',
          vpcId: '',
          iamRole: '',
          ...(typeof engagementData.awsDetails === 'object' &&
          engagementData.awsDetails &&
          !('accountIdSet' in engagementData.awsDetails)
            ? (engagementData.awsDetails as Record<string, string>)
            : {})
        },
        gcpDetails: engagementData.gcpDetails || {
          projectId: '',
          region: '',
          serviceAccount: '',
          zone: ''
        }
      });

      if (engagementData.cloudProvider === 'AWS') {
        const maskedAws = engagementData.awsDetails as Record<string, unknown> | null | undefined;
        if (maskedAws && maskedAws.allRegionsSet) {
          setCloudProviderData((prev) => ({
            ...prev,
            awsDetails: { ...prev.awsDetails, region: AWS_SELECT_ALL_REGIONS_VALUE }
          }));
        }
      }

      if (engagementData.cloudProvider === 'GCP') {
        const maskedGcp = engagementData.gcpDetails as Record<string, unknown> | null | undefined;
        if (maskedGcp && maskedGcp.allRegionsSet) {
          setCloudProviderData((prev) => ({
            ...prev,
            gcpDetails: { ...prev.gcpDetails, region: GCP_SELECT_ALL_REGIONS_VALUE }
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching engagement:', error);
      toast.error('Failed to load engagement');
      navigate('/engagements');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const fetchGroups = useCallback(async () => {
    try {
      const response = await api.get('/groups', { params: { limit: 1000 } });
      setGroups(response.data.groups);
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  }, []);

  const fetchAvailablePlans = useCallback(async () => {
    try {
      const response = await api.get('/service-plans');
      setAvailablePlans(response.data.plans);
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  }, []);

  useEffect(() => {
    if (permissionsLoading) {
      return;
    }
    if (!hasPermission) {
      navigate('/engagements');
      return;
    }

    const initializeData = async () => {
      await Promise.all([
        fetchEngagement(),
        fetchGroups(),
        fetchAvailablePlans()
      ]);
    };

    initializeData();
  }, [hasPermission, permissionsLoading, navigate, fetchEngagement, fetchGroups, fetchAvailablePlans]);

  const validateDateRange = () => {
    if (formData.endDate && formData.startDate) {
      const startDate = new Date(formData.startDate);
      const endDate = new Date(formData.endDate);
      if (endDate <= startDate) {
        setDateError('End Date must be greater than Start Date');
        return false;
      }
    }
    setDateError('');
    return true;
  };

  const handleSaveBasicDetails = async () => {
    // Validate date range before saving
    if (!validateDateRange()) {
      toast.error('Please fix the date validation errors');
      return;
    }

    // Validate required fields (allow 0, reject negative values)
    let hasErrors = false;
    if (formData.minimumServiceFee === null || formData.minimumServiceFee === undefined || formData.minimumServiceFee < 0) {
      setMinimumServiceFeeError('Minimum Service Fee cannot be negative');
      hasErrors = true;
    } else {
      setMinimumServiceFeeError('');
    }
    if (formData.platformCost === null || formData.platformCost === undefined || formData.platformCost < 0) {
      setPlatformCostError('Platform Cost cannot be negative');
      hasErrors = true;
    } else {
      setPlatformCostError('');
    }
    if (hasErrors) {
      return;
    }

    const patchingError = validatePatchingServicesForm(patchingServices);
    if (patchingError) {
      toast.error(patchingError);
      return;
    }

    try {
      setSaving(true);
      const updateData = {
        name: formData.name,
        cloudProvider: formData.cloudProvider,
        customerId: engagement.customerId,
        description: formData.description || null,
        billingType: formData.billingType,
        currency: formData.currency,
        startDate: dateToUTCNoon(formData.startDate),
        endDate: formData.endDate ? dateToUTCNoon(formData.endDate) : null,
        totalBudget: formData.totalBudget,
        contractValue: formData.contractValue,
        minimumServiceFee: formData.minimumServiceFee,
        platformCost: formData.platformCost,
        billable: formData.billable,
        status: formData.status,
        // Email-to-Case Configuration
        emailCaseCreationEnabled: formData.emailCaseCreationEnabled,
        emailMatchAddress: formData.emailMatchAddress || null,
        emailMatchSubject: formData.emailMatchSubject || null,
        emailMatchBody: formData.emailMatchBody || null,
        emailMatchToRecipients: formData.emailMatchToRecipients || null,
        isServiceCreditEngagement: formData.isServiceCreditEngagement,
        complimentaryHours: formData.isServiceCreditEngagement ? formData.complimentaryHours : null,
        patchingServices: patchingServicesToApiPayload(patchingServices)
      };

      await api.put(`/engagements/${id}`, updateData);
      toast.success('Details updated successfully!');
      await fetchEngagement(); // Refresh data
    } catch (error: any) {
      console.error('Error updating engagement:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Failed to update engagement';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCloudProviderDetails = async () => {
    const errs: Record<string, string> = {};
    if (formData.cloudProvider === 'Azure') {
      if (!cloudProviderData.azureDetails.tenantId?.trim()) {
        errs['azure.tenantId'] = 'Tenant ID is required';
      }
      if (!cloudProviderData.azureDetails.clientId?.trim()) {
        errs['azure.clientId'] = 'Client ID is required';
      }
      if (!cloudProviderData.azureDetails.clientSecret?.trim()) {
        errs['azure.clientSecret'] = 'Client Secret is required';
      }
      if (!cloudProviderData.azureDetails.subscriptionId?.trim()) {
        errs['azure.subscriptionId'] = 'Subscription ID is required';
      }
    } else if (formData.cloudProvider === 'AWS') {
      if (!cloudProviderData.awsDetails.accountId?.trim()) {
        errs['aws.accountId'] = 'Account ID is required';
      }
      if (!cloudProviderData.awsDetails.region?.trim()) {
        errs['aws.region'] = 'Select a region or choose Select All Regions';
      }
      if (!cloudProviderData.awsDetails.accessKeyId?.trim()) {
        errs['aws.accessKeyId'] = 'Access Key ID is required';
      }
      if (!cloudProviderData.awsDetails.secretAccessKey?.trim()) {
        errs['aws.secretAccessKey'] = 'Secret Access Key is required';
      }
    } else if (formData.cloudProvider === 'GCP') {
      if (!cloudProviderData.gcpDetails.projectId?.trim()) {
        errs['gcp.projectId'] = 'Project ID is required';
      }
      if (!cloudProviderData.gcpDetails.region?.trim()) {
        errs['gcp.region'] = 'Select a region or choose All regions';
      }
      if (!cloudProviderData.gcpDetails.serviceAccount?.trim()) {
        errs['gcp.serviceAccount'] = 'Service account key (JSON) is required';
      }
    }
    if (Object.keys(errs).length > 0) {
      setCloudProviderFieldErrors(errs);
      toast.error('Please fix the highlighted fields');
      return;
    }
    setCloudProviderFieldErrors({});

    try {
      setSaving(true);
      const awsPayload =
        formData.cloudProvider === 'AWS'
          ? normalizeAwsDetailsForApi(cloudProviderData.awsDetails)
          : null;
      const gcpPayload =
        formData.cloudProvider === 'GCP'
          ? normalizeGcpDetailsForApi(cloudProviderData.gcpDetails)
          : null;
      const updateData = {
        name: formData.name,
        cloudProvider: formData.cloudProvider,
        customerId: engagement.customerId,
        startDate: formData.startDate ? dateToUTCNoon(formData.startDate) : engagement.startDate,
        minimumServiceFee: formData.minimumServiceFee,
        platformCost: formData.platformCost,
        azureDetails: formData.cloudProvider === 'Azure' ? cloudProviderData.azureDetails : null,
        awsDetails: awsPayload,
        gcpDetails: gcpPayload
      };

      await api.put(`/engagements/${id}`, updateData);
      toast.success('Cloud provider details updated successfully!');
      await fetchEngagement(); // Refresh data
    } catch (error: any) {
      // Error updating cloud provider details - log the structure but not sensitive values
      console.error('[Cloud Provider Update Error]', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to update cloud provider details';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const clearCloudProviderFieldError = (errorKey: string) => {
    setCloudProviderFieldErrors((prev) => {
      if (!prev[errorKey]) return prev;
      const next = { ...prev };
      delete next[errorKey];
      return next;
    });
  };

  const cpFieldClass = (errorKey: string) =>
    `mt-1 block w-full rounded-md border shadow-sm focus:border-blue-500 focus:ring-blue-500 ${
      cloudProviderFieldErrors[errorKey] ? 'border-red-500' : 'border-gray-300'
    }`;

  const handleAssignGroups = async (groupIds: string[]) => {
    try {
      await api.post(`/engagements/${id}/assign-groups`, {
        groupIds,
        defaultHourlyRate: 100,
        defaultRole: 'DEVELOPER'
      });
      toast.success('Groups assigned successfully!');
      await fetchEngagement(); // Refresh data
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to assign groups';
      toast.error(errorMessage);
    }
  };

  const [syncing, setSyncing] = useState(false);

  const handleSyncGroups = async () => {
    try {
      setSyncing(true);
      const response = await api.post(`/engagements/${id}/sync-groups`);
      const { agentsAdded, addedMembers } = response.data;
      if (agentsAdded > 0) {
        toast.success(`Synced ${agentsAdded} new member(s): ${addedMembers?.join(', ') || ''}`);
      } else {
        toast.success('All group members are already synced');
      }
      await fetchEngagement(); // Refresh data
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to sync groups';
      toast.error(errorMessage);
    } finally {
      setSyncing(false);
    }
  };


  const handleAssignPlan = async (planId: string) => {
    try {
      await api.post(`/engagements/${id}/plans/${planId}`);
      toast.success('Plan assigned successfully!');
      await fetchEngagement(); // Refresh data
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to assign plan';
      toast.error(errorMessage);
    }
  };

  const handleUnassignPlan = async (planId: string) => {
    try {
      await api.delete(`/engagements/${id}/plans/${planId}`);
      toast.success('Plan unassigned successfully!');
      await fetchEngagement(); // Refresh data
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to unassign plan';
      toast.error(errorMessage);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!engagement) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900">Engagement not found</h3>
          <p className="mt-2 text-sm text-gray-500">The engagement you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/engagements')}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            Back to Engagements
          </button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'details', name: 'Details', icon: DocumentTextIcon },
    { id: 'agents', name: 'Agents', icon: UserGroupIcon },
    { id: 'plans', name: 'Plans', icon: CalendarIcon },
    { id: 'serviceproviders', name: 'Service Providers', icon: CloudIcon },
  ];

  return (
    <div className="max-w-7xl mx-auto py-8 px-4">
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <Link
            to="/engagements"
            className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700"
          >
            <ArrowLeftIcon className="mr-2 h-4 w-4" />
            Back to Engagements
          </Link>
        </div>
        <h1 className="text-3xl font-bold text-gray-900">Edit Engagement</h1>
        <p className="mt-2 text-gray-600">{engagement.name} • {engagement.number}</p>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-8">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`group inline-flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="mr-2 h-5 w-5" />
                {tab.name}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white shadow-lg rounded-lg p-6">
        {/* Details Tab */}
        {activeTab === 'details' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">Engagement Details</h3>
              <button
                onClick={handleSaveBasicDetails}
                disabled={saving}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">Engagement Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Cloud Provider *</label>
                <select
                  value={formData.cloudProvider}
                  onChange={(e) => setFormData({ ...formData, cloudProvider: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  required
                >
                  <option value="">Select Cloud Provider</option>
                  <option value="Azure">Azure</option>
                  <option value="AWS">AWS</option>
                  <option value="GCP">GCP</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Customer</label>
                <div className="mt-1 text-sm text-gray-900 font-medium">
                  {engagement?.Customer?.accountName} ({engagement?.Customer?.accountShortName})
                </div>
                <p className="mt-1 text-sm text-gray-500">Customer cannot be changed after engagement creation</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                >
                  <option value="DRAFT">Draft</option>
                  <option value="ACTIVE">Active</option>
                  <option value="ON_HOLD">On Hold</option>
                  <option value="COMPLETED">Completed</option>
                  <option value="CANCELLED">Cancelled</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Billing Type</label>
                <select
                  value={formData.billingType}
                  onChange={(e) => setFormData({ ...formData, billingType: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                >
                  <option value="HOURLY">Hourly</option>
                  <option value="FIXED_PRICE">Fixed Price</option>
                  <option value="RETAINER">Retainer</option>
                  <option value="MILESTONE">Milestone</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Currency</label>
                <select
                  value={formData.currency}
                  onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                >
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                  <option value="CAD">CAD</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Start Date *</label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => {
                    setFormData({ ...formData, startDate: e.target.value });
                    // Clear error when start date changes
                    if (dateError) {
                      setDateError('');
                    }
                  }}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">End Date</label>
                <input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => {
                    const newEndDate = e.target.value;
                    setFormData({ ...formData, endDate: newEndDate });
                    // Validate immediately when end date changes
                    if (newEndDate && formData.startDate) {
                      const startDate = new Date(formData.startDate);
                      const endDate = new Date(newEndDate);
                      if (endDate <= startDate) {
                        setDateError('End Date must be greater than Start Date');
                      } else {
                        setDateError('');
                      }
                    } else {
                      setDateError('');
                    }
                  }}
                  className={`mt-1 block w-full rounded-md shadow-sm focus:ring-blue-500 ${
                    dateError ? 'border-red-300 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'
                  }`}
                />
                {dateError && (
                  <p className="mt-1 text-sm text-red-600">{dateError}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Minimum Service Fee *</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.minimumServiceFee ?? ''}
                  onChange={(e) => {
                    const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                    setFormData({ ...formData, minimumServiceFee: value });
                    if (minimumServiceFeeError) setMinimumServiceFeeError('');
                  }}
                  className={`mt-1 block w-full rounded-md shadow-sm focus:ring-blue-500 ${
                    minimumServiceFeeError ? 'border-red-300 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'
                  }`}
                />
                {minimumServiceFeeError && (
                  <p className="mt-1 text-sm text-red-600">{minimumServiceFeeError}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Platform Cost *</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.platformCost ?? ''}
                  onChange={(e) => {
                    const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                    setFormData({ ...formData, platformCost: value });
                    if (platformCostError) setPlatformCostError('');
                  }}
                  className={`mt-1 block w-full rounded-md shadow-sm focus:ring-blue-500 ${
                    platformCostError ? 'border-red-300 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'
                  }`}
                />
                {platformCostError && (
                  <p className="mt-1 text-sm text-red-600">{platformCostError}</p>
                )}
              </div>

              <div className="sm:col-span-2">
                <fieldset>
                  <legend className="block text-sm font-medium text-gray-700">Billable</legend>
                  <div className="mt-2 flex items-center gap-6">
                    <label className="inline-flex items-center gap-2 text-sm text-gray-900">
                      <input
                        type="radio"
                        name="billable"
                        checked={formData.billable}
                        onChange={() => setFormData((prev) => ({ ...prev, billable: true }))}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                      />
                      Yes
                    </label>
                    <label className="inline-flex items-center gap-2 text-sm text-gray-900">
                      <input
                        type="radio"
                        name="billable"
                        checked={!formData.billable}
                        onChange={() => setFormData((prev) => ({ ...prev, billable: false }))}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                      />
                      No
                    </label>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Non-billable engagements count toward &quot;Non Billable Hours&quot; on the bandwidth allocation dashboard.
                  </p>
                </fieldset>
              </div>

              <div className="sm:col-span-2">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isServiceCreditEngagement"
                    checked={formData.isServiceCreditEngagement}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setFormData((prev) => ({
                        ...prev,
                        isServiceCreditEngagement: checked,
                        complimentaryHours: checked ? prev.complimentaryHours : null
                      }));
                    }}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isServiceCreditEngagement" className="ml-2 block text-sm text-gray-900">
                    Service Credit Engagement
                  </label>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  When enabled, effort costs are not billed, this engagement is excluded from consolidated customer
                  charges, and logged hours still appear in reporting. Use Complimentary Hours to record the agreed pool.
                </p>
                {formData.isServiceCreditEngagement && (
                  <div className="mt-3 max-w-xs">
                    <label className="block text-sm font-medium text-gray-700">Complimentary Hours</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.complimentaryHours ?? ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        setFormData({
                          ...formData,
                          complimentaryHours: v === '' ? null : parseFloat(v)
                        });
                      }}
                      placeholder="e.g. 40"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                )}
              </div>
            </div>

            <PatchingServicesSection
              value={patchingServices}
              onChange={setPatchingServices}
              currency={formData.currency}
            />

            {/* Email-to-Case Configuration */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <h4 className="text-md font-medium text-gray-900 mb-4">Email-to-Case Configuration</h4>

              <div className="flex items-center mb-4">
                <input
                  type="checkbox"
                  id="emailCaseCreationEnabled"
                  checked={formData.emailCaseCreationEnabled}
                  onChange={(e) => setFormData({ ...formData, emailCaseCreationEnabled: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="emailCaseCreationEnabled" className="ml-2 block text-sm text-gray-900">
                  Can create Case via Email
                </label>
              </div>

              {formData.emailCaseCreationEnabled && (
                <div className="grid grid-cols-1 gap-4 ml-6 p-4 bg-gray-50 rounded-md">
                  <p className="text-sm text-gray-600 mb-2">
                    Configure string patterns to match emails. At least one pattern is required.
                    Matching order: From → To (recipient/DL) → Subject → Body. Patterns are case-insensitive and unique across engagements.
                  </p>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">String in Sender Email (From)</label>
                    <input
                      type="text"
                      value={formData.emailMatchAddress}
                      onChange={(e) => setFormData({ ...formData, emailMatchAddress: e.target.value })}
                      placeholder="e.g., @acme.com or john.doe"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">To / Distribution List (recipient email)</label>
                    <input
                      type="text"
                      value={formData.emailMatchToRecipients}
                      onChange={(e) => setFormData({ ...formData, emailMatchToRecipients: e.target.value })}
                      placeholder="e.g., support-dl@acme.com or comma-separated addresses"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">String in Subject</label>
                    <input
                      type="text"
                      value={formData.emailMatchSubject}
                      onChange={(e) => setFormData({ ...formData, emailMatchSubject: e.target.value })}
                      placeholder="e.g., [ACME-SUPPORT] or Ticket#"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">String in Message Body</label>
                    <input
                      type="text"
                      value={formData.emailMatchBody}
                      onChange={(e) => setFormData({ ...formData, emailMatchBody: e.target.value })}
                      placeholder="e.g., EngagementRef:ENG001"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Agents Tab */}
        {activeTab === 'agents' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">Manage Agents</h3>
              <div className="flex items-center space-x-3">
                {engagement.EngagementAgent && engagement.EngagementAgent.length > 0 && (
                  <button
                    onClick={handleSyncGroups}
                    disabled={syncing}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    title="Sync any new members added to the assigned groups"
                  >
                    <ArrowPathIcon className={`mr-2 h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
                    {syncing ? 'Syncing...' : 'Sync Members'}
                  </button>
                )}
                <GroupSelector
                  groups={groups}
                  onAssignGroups={handleAssignGroups}
                  currentlyAssignedGroupIds={engagement.EngagementAgent?.map((agent: any) => agent.Group?.id).filter(Boolean) || []}
                />
              </div>
            </div>

            {engagement.EngagementAgent && engagement.EngagementAgent.length > 0 ? (
              <AgentGroupTree
                agents={engagement.EngagementAgent}
                engagementId={id!}
                onRateUpdated={fetchEngagement}
              />
            ) : (
              <div className="text-center py-12">
                <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No agents assigned</h3>
                <p className="mt-1 text-sm text-gray-500">Assign groups to add agents to this engagement.</p>
              </div>
            )}
          </div>
        )}

        {/* Plans Tab */}
        {activeTab === 'plans' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">Service Plans</h3>
              <PlanSelector 
                availablePlans={availablePlans}
                assignedPlans={assignedPlans}
                onAssignPlan={handleAssignPlan}
              />
            </div>

            {assignedPlans && assignedPlans.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Offer</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned Date</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {assignedPlans.map((assignment: any) => (
                      <tr key={assignment.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{assignment.ServicePlan.name}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex px-2 py-1 text-xs font-medium rounded-md bg-blue-50 text-blue-700">
                            {assignment.ServicePlan.type?.replace('_', ' ') || 'N/A'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{assignment.ServicePlan.Offer?.name || 'N/A'}</div>
                          <div className="text-xs text-gray-500">{assignment.ServicePlan.Offer?.offerType?.replace('_', ' ') || ''}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-600 max-w-xs truncate">
                            {assignment.ServicePlan.description || 'No description available'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            assignment.status === 'ACTIVE' ? 'bg-green-100 text-green-800' :
                            assignment.status === 'COMPLETED' ? 'bg-blue-100 text-blue-800' :
                            assignment.status === 'ON_HOLD' ? 'bg-yellow-100 text-yellow-800' :
                            assignment.status === 'CANCELLED' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {assignment.status || 'ACTIVE'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(assignment.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => handleUnassignPlan(assignment.planId)}
                            className="text-red-600 hover:text-red-800"
                            title="Remove plan"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <CalendarIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No plans assigned</h3>
                <p className="mt-1 text-sm text-gray-500">Assign service plans to structure this engagement.</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'serviceproviders' && id && (
          <ServiceProvidersTab
            engagementId={id}
            engagementName={engagement.name}
            editMode
            onRefreshEngagement={fetchEngagement}
          />
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between pt-6 mt-8 border-t border-gray-200">
          <button
            onClick={() => navigate(`/engagements/${id}`)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded-md text-sm font-medium"
          >
            Back to Details
          </button>
        </div>
      </div>
    </div>
  );
};

// Group Selector Component
const GroupSelector: React.FC<{
  groups: any[];
  onAssignGroups: (groupIds: string[]) => void;
  currentlyAssignedGroupIds: string[];
}> = ({ groups, onAssignGroups, currentlyAssignedGroupIds }) => {
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [availableGroups, setAvailableGroups] = useState<any[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [availableSearchTerm, setAvailableSearchTerm] = useState('');

  // Filter groups based on search terms
  const filteredSelectedGroups = selectedGroups
    .map(id => groups.find(g => g.id === id))
    .filter(Boolean)
    .filter(group => 
      group.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      group.description.toLowerCase().includes(searchTerm.toLowerCase())
    );

  const filteredAvailableGroups = availableGroups.filter(group =>
    group.name.toLowerCase().includes(availableSearchTerm.toLowerCase()) ||
    group.description.toLowerCase().includes(availableSearchTerm.toLowerCase())
  );

  const handleShowModal = () => {
    // Initialize with currently assigned groups
    const uniqueAssignedGroupIds = Array.from(new Set(currentlyAssignedGroupIds));
    setSelectedGroups(uniqueAssignedGroupIds);
    setAvailableGroups(groups.filter(group => !uniqueAssignedGroupIds.includes(group.id)));
    setSearchTerm('');
    setAvailableSearchTerm('');
    setShowModal(true);
  };

  const handleMoveToSelected = (groupId: string) => {
    const group = availableGroups.find(g => g.id === groupId);
    if (group) {
      setSelectedGroups([...selectedGroups, groupId]);
      setAvailableGroups(availableGroups.filter(g => g.id !== groupId));
    }
  };

  const handleMoveToAvailable = (groupId: string) => {
    const group = groups.find(g => g.id === groupId);
    if (group) {
      setAvailableGroups([...availableGroups, group]);
      setSelectedGroups(selectedGroups.filter(id => id !== groupId));
    }
  };

  const handleAssign = () => {
    // Find newly added groups (groups that are selected but weren't originally assigned)
    const originallyAssigned = [...new Set(currentlyAssignedGroupIds)];
    const newlySelected = selectedGroups.filter(id => !originallyAssigned.includes(id));
    
    // If there are new groups to assign, call the assignment function
    if (newlySelected.length > 0) {
      onAssignGroups(newlySelected);
    }
    
    // Note: Removing groups would require additional backend endpoints to remove agents by group
    // For now, we'll just assign new groups. Group removal can be handled by individual agent removal.
    
    setSelectedGroups([]);
    setAvailableGroups([]);
    setShowModal(false);
  };

  return (
    <>
      <button
        onClick={handleShowModal}
        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
      >
        <PlusIcon className="mr-2 h-4 w-4" />
        Assign Groups
      </button>

      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-5xl w-full mx-4 max-h-[80vh] flex flex-col">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Select Groups to Assign</h3>
              <p className="text-sm text-gray-500 mt-1">Choose groups to assign their members as agents to this engagement</p>
            </div>

            <div className="flex-1 flex min-h-0">
              {/* Available Groups Panel */}
              <div className="flex-1 flex flex-col border-r border-gray-200">
                <div className="p-4 border-b border-gray-200">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search..."
                      value={availableSearchTerm}
                      onChange={(e) => setAvailableSearchTerm(e.target.value)}
                      className="w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                  </div>
                  <h4 className="text-sm font-medium text-gray-900 mt-3">Available Groups</h4>
                </div>
                <div className="flex-1 overflow-y-auto p-4">
                  <div className="space-y-2">
                    {filteredAvailableGroups.length === 0 ? (
                      <div className="text-center py-8 text-gray-500 text-sm">
                        {availableSearchTerm ? 'No groups match your search' : 'No groups available'}
                      </div>
                    ) : (
                      filteredAvailableGroups.map((group) => (
                        <div
                          key={group.id}
                          onClick={() => handleMoveToSelected(group.id)}
                          className="p-3 border border-gray-200 rounded-md cursor-pointer hover:bg-blue-50 hover:border-blue-300 transition-colors"
                        >
                          <div className="text-sm font-medium text-gray-900">{group.name}</div>
                          <div className="text-xs text-gray-500 mt-1">{group.description}</div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>

              {/* Control Buttons */}
              <div className="w-16 flex flex-col items-center justify-center space-y-2 bg-gray-50">
                <button
                  onClick={() => {
                    // Move all filtered available groups to selected
                    const groupsToMove = filteredAvailableGroups.map(g => g.id);
                    setSelectedGroups([...selectedGroups, ...groupsToMove]);
                    setAvailableGroups(availableGroups.filter(g => !groupsToMove.includes(g.id)));
                  }}
                  disabled={filteredAvailableGroups.length === 0}
                  className="p-2 text-gray-600 hover:text-gray-900 disabled:text-gray-300"
                  title="Move all to selected"
                >
                  <ChevronRightIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={() => {
                    // Move all selected groups back to available
                    const groupsToMove = selectedGroups.map(id => groups.find(g => g.id === id)).filter(Boolean);
                    setAvailableGroups([...availableGroups, ...groupsToMove]);
                    setSelectedGroups([]);
                  }}
                  disabled={selectedGroups.length === 0}
                  className="p-2 text-gray-600 hover:text-gray-900 disabled:text-gray-300"
                  title="Move all to available"
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                </button>
              </div>

              {/* Selected Groups Panel */}
              <div className="flex-1 flex flex-col">
                <div className="p-4 border-b border-gray-200">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                  </div>
                  <h4 className="text-sm font-medium text-gray-900 mt-3">
                    {selectedGroups.length > 0 ? `Selected Groups (${selectedGroups.length})` : 'Selected Groups'}
                  </h4>
                </div>
                <div className="flex-1 overflow-y-auto p-4">
                  <div className="space-y-2">
                    {filteredSelectedGroups.length === 0 ? (
                      <div className="text-center py-8 text-gray-500 text-sm">
                        {searchTerm ? 'No selected groups match your search' : 'No groups selected'}
                      </div>
                    ) : (
                      filteredSelectedGroups.map((group) => (
                        <div
                          key={group.id}
                          onClick={() => handleMoveToAvailable(group.id)}
                          className="p-3 border border-gray-200 rounded-md cursor-pointer hover:bg-red-50 hover:border-red-300 transition-colors"
                        >
                          <div className="text-sm font-medium text-gray-900">{group.name}</div>
                          <div className="text-xs text-gray-500 mt-1">{group.description}</div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowModal(false);
                  setSelectedGroups([]);
                  setAvailableGroups([]);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={handleAssign}
                disabled={selectedGroups.filter(id => !currentlyAssignedGroupIds.includes(id)).length === 0}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-md"
              >
                Assign New ({selectedGroups.filter(id => !currentlyAssignedGroupIds.includes(id)).length})
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Confirmation Modal Component
const ConfirmationModal: React.FC<{
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
  variant?: 'danger' | 'warning';
}> = ({ isOpen, title, message, confirmText = 'Confirm', cancelText = 'Cancel', onConfirm, onCancel, isLoading = false, variant = 'danger' }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
        <div className="flex items-start space-x-4">
          <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${variant === 'danger' ? 'bg-red-100' : 'bg-yellow-100'}`}>
            <svg className={`h-6 w-6 ${variant === 'danger' ? 'text-red-600' : 'text-yellow-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-medium text-gray-900">{title}</h3>
            <p className="mt-2 text-sm text-gray-500">{message}</p>
          </div>
        </div>
        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-50"
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className={`px-4 py-2 text-sm font-medium text-white rounded-md disabled:opacity-50 flex items-center ${
              variant === 'danger' ? 'bg-red-600 hover:bg-red-700' : 'bg-yellow-600 hover:bg-yellow-700'
            }`}
          >
            {isLoading && (
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

// Agent Row Component
const AgentRow: React.FC<{
  agent: any;
  onRateUpdated: () => void;
  onAgentDeleted: () => void;
}> = ({ agent, onRateUpdated, onAgentDeleted }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedRate, setEditedRate] = useState(agent.hourlyRate);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await api.delete(`/engagements/${agent.engagementId}/agents/${agent.id}`);
      toast.success(`${agent.User.firstName} ${agent.User.lastName} removed from engagement`);
      setShowDeleteModal(false);
      onAgentDeleted();
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to remove agent';
      toast.error(errorMessage);
    } finally {
      setDeleting(false);
    }
  };

  const handleSave = async () => {
    if (editedRate < 0) {
      toast.error('Hourly rate cannot be negative');
      return;
    }

    try {
      setSaving(true);
      await api.put(`/engagements/${agent.engagementId}/agents/${agent.id}/rate`, {
        hourlyRate: parseFloat(editedRate)
      });
      toast.success('Hourly rate updated successfully');
      setIsEditing(false);
      onRateUpdated(); // Refresh the engagement data
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to update hourly rate';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditedRate(agent.hourlyRate);
    setIsEditing(false);
  };

  return (
    <tr>
      <td className="px-6 py-4 whitespace-nowrap pl-12">
        <div className="text-sm font-medium text-gray-900">
          {agent.User.firstName} {agent.User.lastName}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className="text-sm text-gray-500">{agent.User.email}</span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        {isEditing ? (
          <div className="flex items-center space-x-2">
            <div className="flex items-center">
              <span className="text-sm text-gray-700">$</span>
              <input
                type="number"
                value={editedRate}
                onChange={(e) => setEditedRate(e.target.value)}
                min="0"
                step="0.01"
                className="ml-1 w-24 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                disabled={saving}
              />
              <span className="ml-1 text-sm text-gray-700">/hr</span>
            </div>
            <button
              onClick={handleSave}
              disabled={saving}
              className="text-green-600 hover:text-green-800 disabled:opacity-50"
              title="Save"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </button>
            <button
              onClick={handleCancel}
              disabled={saving}
              className="text-red-600 hover:text-red-800 disabled:opacity-50"
              title="Cancel"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        ) : (
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-900">${agent.hourlyRate}/hr</span>
            <button
              onClick={() => setIsEditing(true)}
              className="text-blue-600 hover:text-blue-800"
              title="Edit hourly rate"
            >
              <PencilIcon className="h-4 w-4" />
            </button>
          </div>
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right">
        <button
          onClick={() => setShowDeleteModal(true)}
          disabled={deleting}
          className="text-red-600 hover:text-red-800 disabled:opacity-50"
          title="Remove from engagement"
        >
          <TrashIcon className="h-4 w-4" />
        </button>
        <ConfirmationModal
          isOpen={showDeleteModal}
          title="Remove Agent"
          message={`Are you sure you want to remove ${agent.User.firstName} ${agent.User.lastName} from this engagement? This action cannot be undone.`}
          confirmText="Remove"
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteModal(false)}
          isLoading={deleting}
          variant="danger"
        />
      </td>
    </tr>
  );
};

// Agent Group Tree Component
const AgentGroupTree: React.FC<{
  agents: any[];
  engagementId: string;
  onRateUpdated: () => void;
}> = ({ agents, engagementId, onRateUpdated }) => {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [groupRate, setGroupRate] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [deletingGroupId, setDeletingGroupId] = useState<string | null>(null);
  const [deleteGroupModal, setDeleteGroupModal] = useState<{ groupId: string; groupName: string; agentCount: number } | null>(null);

  const handleDeleteGroup = async () => {
    if (!deleteGroupModal) return;

    try {
      setDeletingGroupId(deleteGroupModal.groupId);
      await api.delete(`/engagements/${engagementId}/groups/${deleteGroupModal.groupId}`);
      toast.success(`All agents from "${deleteGroupModal.groupName}" removed from engagement`);
      setDeleteGroupModal(null);
      onRateUpdated(); // Refresh the engagement data
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to remove group';
      toast.error(errorMessage);
    } finally {
      setDeletingGroupId(null);
    }
  };

  // Group agents by their group
  const groupedAgents = agents.reduce((acc: any, agent: any) => {
    const groupId = agent.Group?.id || 'ungrouped';
    const groupName = agent.Group?.name || 'Ungrouped';
    if (!acc[groupId]) {
      acc[groupId] = { groupId, groupName, agents: [] };
    }
    acc[groupId].agents.push(agent);
    return acc;
  }, {});

  const toggleGroup = (groupId: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId);
    } else {
      newExpanded.add(groupId);
    }
    setExpandedGroups(newExpanded);
  };

  const handleGroupRateSave = async (groupId: string) => {
    if (groupRate === '' || groupRate === null || groupRate === undefined || parseFloat(groupRate) < 0) {
      toast.error('Hourly rate cannot be negative');
      return;
    }

    try {
      setSaving(true);
      await api.put(`/engagements/${engagementId}/groups/${groupId}/rate`, {
        hourlyRate: parseFloat(groupRate)
      });
      toast.success('Group rate updated for all agents');
      setEditingGroupId(null);
      setGroupRate('');
      onRateUpdated();
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to update group rate';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const startEditingGroup = (groupId: string, currentRate: number) => {
    setEditingGroupId(groupId);
    setGroupRate(currentRate.toString());
  };

  return (
    <div className="space-y-2">
      {Object.values(groupedAgents).map((group: any) => {
        const isExpanded = expandedGroups.has(group.groupId);
        const avgRate = group.agents.reduce((sum: number, a: any) => sum + Number(a.hourlyRate), 0) / group.agents.length;

        return (
          <div key={group.groupId} className="border border-gray-200 rounded-lg overflow-hidden">
            {/* Group Header */}
            <div
              className="bg-gray-50 px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-gray-100"
              onClick={() => toggleGroup(group.groupId)}
            >
              <div className="flex items-center space-x-3">
                <svg
                  className={`h-5 w-5 text-gray-500 transform transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
                <UserGroupIcon className="h-5 w-5 text-blue-500" />
                <span className="font-medium text-gray-900">{group.groupName}</span>
                <span className="text-sm text-gray-500">({group.agents.length} agent{group.agents.length > 1 ? 's' : ''})</span>
              </div>

              <div className="flex items-center space-x-4" onClick={(e) => e.stopPropagation()}>
                {editingGroupId === group.groupId ? (
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-700">$</span>
                    <input
                      type="number"
                      value={groupRate}
                      onChange={(e) => setGroupRate(e.target.value)}
                      min="0"
                      step="0.01"
                      className="w-24 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                      disabled={saving}
                    />
                    <span className="text-sm text-gray-700">/hr</span>
                    <button
                      onClick={() => handleGroupRateSave(group.groupId)}
                      disabled={saving}
                      className="text-green-600 hover:text-green-800 disabled:opacity-50"
                      title="Save for all agents"
                    >
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </button>
                    <button
                      onClick={() => { setEditingGroupId(null); setGroupRate(''); }}
                      disabled={saving}
                      className="text-red-600 hover:text-red-800 disabled:opacity-50"
                      title="Cancel"
                    >
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600">${avgRate.toFixed(2)}/hr</span>
                    <button
                      onClick={() => startEditingGroup(group.groupId, avgRate)}
                      className="text-blue-600 hover:text-blue-800 px-2 py-1 text-xs bg-blue-50 rounded"
                      title="Set rate for all agents in this group"
                    >
                      Set Group Rate
                    </button>
                    {group.groupId !== 'ungrouped' && (
                      <button
                        onClick={() => setDeleteGroupModal({ groupId: group.groupId, groupName: group.groupName, agentCount: group.agents.length })}
                        disabled={deletingGroupId === group.groupId}
                        className="text-red-600 hover:text-red-800 px-2 py-1 text-xs bg-red-50 rounded disabled:opacity-50"
                        title="Remove all agents in this group from engagement"
                      >
                        {deletingGroupId === group.groupId ? 'Removing...' : 'Remove Group'}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Agents Table (Expanded) */}
            {isExpanded && (
              <div className="border-t border-gray-200">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider pl-12">Agent</th>
                      <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                      <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hourly Rate</th>
                      <th className="px-6 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {group.agents.map((agent: any) => (
                      <AgentRow
                        key={agent.id}
                        agent={agent}
                        onRateUpdated={onRateUpdated}
                        onAgentDeleted={onRateUpdated}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}

      {/* Delete Group Confirmation Modal */}
      <ConfirmationModal
        isOpen={!!deleteGroupModal}
        title="Remove Group"
        message={deleteGroupModal ? `Are you sure you want to remove all ${deleteGroupModal.agentCount} agent(s) from "${deleteGroupModal.groupName}" from this engagement? This action cannot be undone.` : ''}
        confirmText="Remove All"
        onConfirm={handleDeleteGroup}
        onCancel={() => setDeleteGroupModal(null)}
        isLoading={!!deletingGroupId}
        variant="danger"
      />
    </div>
  );
};

// Plan Selector Component
const PlanSelector: React.FC<{
  availablePlans: any[];
  assignedPlans: any[];
  onAssignPlan: (planId: string) => void;
}> = ({ availablePlans, assignedPlans, onAssignPlan }) => {
  const [selectedPlan, setSelectedPlan] = useState('');
  const [showModal, setShowModal] = useState(false);

  const assignedPlanIds = assignedPlans.map(ap => ap.planId);
  const unassignedPlans = availablePlans.filter(plan => !assignedPlanIds.includes(plan.id));

  const handleAssign = () => {
    if (selectedPlan) {
      onAssignPlan(selectedPlan);
      setSelectedPlan('');
      setShowModal(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
      >
        <PlusIcon className="mr-2 h-4 w-4" />
        Assign Plan
      </button>

      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Assign Service Plan</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Select Plan</label>
                <select
                  value={selectedPlan}
                  onChange={(e) => setSelectedPlan(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                >
                  <option value="">Choose a plan...</option>
                  {unassignedPlans.map((plan) => (
                    <option key={plan.id} value={plan.id}>
                      {plan.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={handleAssign}
                disabled={!selectedPlan}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-md"
              >
                Assign Plan
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EngagementEdit;
